(function () {
  'use strict';

  const dataElement = document.getElementById('MarketLocalizationData');
  if (!dataElement) return;

  const data = JSON.parse(dataElement.textContent);
  const policy = window.BarkLocalizationPolicy;
  const dialog = document.getElementById('MarketLocalizationDialog');
  const form = document.getElementById('MarketLocalizationForm');
  const countryInput = form.querySelector('[data-localization-country]');
  const languageInput = form.querySelector('[data-localization-language]');
  const choiceSelect = form.querySelector('[data-localization-choice]');
  const options = form.querySelector('[data-localization-options]');
  const errorMessage = form.querySelector('[data-localization-error]');
  const manualChoices = policy.manualChoices(data.current, data.countries);
  const preferenceKey = 'bark:localization:preference';
  // Markets on separate domains (barkdna.se) cannot read this origin's storage, so an in-flight
  // choice travels in this query parameter and is confirmed and removed on arrival.
  const handoffKey = 'bark_localization';
  const day = 24 * 60 * 60 * 1000;
  const pendingLifetime = 5 * 60 * 1000;
  let returnFocus = null;
  let pendingRecommendation = null;
  let pagePreference = null;

  function readPreference() {
    const inMemory = policy.preferenceState(JSON.stringify(pagePreference), Date.now(), data.countries);
    if (inMemory) return inMemory;
    try {
      return policy.preferenceState(localStorage.getItem(preferenceKey), Date.now(), data.countries, data.current);
    } catch (error) {
      // Browsing and the manual selector remain available when storage is blocked.
      return null;
    }
  }

  function savePreference(preference) {
    pagePreference = preference;
    try {
      localStorage.setItem(preferenceKey, JSON.stringify(preference));
    } catch (error) {
      // Storage is optional; it must never prevent a country/language change.
    }
  }

  function clearPreference() {
    pagePreference = null;
    try {
      localStorage.removeItem(preferenceKey);
    } catch (error) {
      // Some private browsing policies disable storage entirely.
    }
  }

  function alternateLinks() {
    // Shopify generates one hreflang alternate per country/language, including other market domains.
    return [...document.querySelectorAll('link[rel="alternate"][hreflang]')]
      .filter(link => {
        try {
          return new URL(link.href).protocol === location.protocol;
        } catch (error) {
          return false;
        }
      });
  }

  function destinationOrigin(choice) {
    // Primary-market languages are published as plain codes (da), other markets as language-country (sv-se).
    const wanted = [`${choice.language}-${choice.country}`, choice.language].map(code => code.toLowerCase());
    for (const code of wanted) {
      const link = alternateLinks().find(item => item.hreflang.toLowerCase() === code);
      if (link) return new URL(link.href).origin;
    }
    return null;
  }

  function encodeHandoff(preference) {
    return preference.country
      ? [preference.country, preference.language].join('|')
      : [preference.market, preference.currency, preference.language].join('|');
  }

  function decodeHandoff(raw) {
    const parts = typeof raw === 'string' ? raw.split('|') : [];
    const expiresAt = Date.now() + pendingLifetime;
    if (parts.length === 2) return { kind: 'pending', country: parts[0], language: parts[1], expiresAt };
    if (parts.length === 3) return { kind: 'pending', market: parts[0], currency: parts[1], language: parts[2], expiresAt };
    return null;
  }

  function consumeHandoff() {
    const url = new URL(location.href);
    if (!url.searchParams.has(handoffKey)) return;
    const pending = decodeHandoff(url.searchParams.get(handoffKey));
    url.searchParams.delete(handoffKey);
    try {
      history.replaceState(history.state, '', url.href);
    } catch (error) {
      // A visible parameter is harmless; the choice is still confirmed below.
    }
    // Page memory only: the policy validates it and confirmPendingChoice decides whether to keep it.
    if (pending) pagePreference = pending;
  }

  function confirmPendingChoice() {
    const preference = readPreference();
    if (!preference || preference.kind !== 'pending') return;
    const matches = preference.language === data.current.language && (preference.country
      ? preference.country === data.current.country
      : preference.market === data.current.market && preference.currency === data.current.currency);
    if (matches) {
      savePreference({ kind: 'selected', country: data.current.country, language: data.current.language, expiresAt: Date.now() + 60 * day });
    } else {
      clearPreference();
    }
  }

  function countryByCode(code) {
    return data.countries.find(country => country.code === code);
  }

  function copyFor(language) {
    return data.translations[language] || data.translations.en;
  }

  function setCopy(language, mode, choice) {
    const copy = copyFor(language);
    dialog.lang = data.translations[language] ? language : 'en';
    const title = dialog.querySelector('[data-localization-title]');
    const description = dialog.querySelector('[data-localization-description]');
    options.hidden = mode === 'recommendation';
    description.hidden = mode === 'manual';
    errorMessage.hidden = true;
    if (mode === 'recommendation') {
      const countryNames = new Intl.DisplayNames([dialog.lang], { type: 'region' });
      const languageNames = new Intl.DisplayNames([dialog.lang], { type: 'language' });
      const country = countryByCode(choice.country);
      title.textContent = copy.recommendationTitle.replace('{language}', languageNames.of(choice.language));
      description.textContent = copy.recommendationDescription.replace('{country}', countryNames.of(country.code)).replace('{currency}', country.currency);
      dialog.setAttribute('aria-describedby', description.id);
    } else {
      title.textContent = copy.title;
      description.textContent = '';
      dialog.removeAttribute('aria-describedby');
    }
    dialog.querySelector('[data-localization-submit]').textContent = copy.continue;
    dialog.querySelector('[data-localization-stay]').textContent = copy.stay;
    dialog.querySelector('[data-localization-close]').setAttribute('aria-label', copy.close);
  }

  function choiceKey(choice) {
    return [choice.market, choice.currency, choice.language].join('|');
  }

  function choiceLabel(item) {
    return `${item.name.charAt(0).toLocaleUpperCase(item.language)}${item.name.slice(1)} \u00b7 ${item.currency}`;
  }

  function populateChoices() {
    choiceSelect.replaceChildren(...manualChoices.map(item => new Option(choiceLabel(item), choiceKey(item))));
  }

  function setChoice(choice) {
    countryInput.value = choice.country || '';
    languageInput.value = choice.language;
    errorMessage.hidden = true;
  }

  function openDialog(choice, mode, trigger) {
    if (dialog.open) return;
    if (mode === 'recommendation' && !policy.validChoice(choice, data.countries)) return;
    if (mode === 'manual') {
      if (!manualChoices.length) return;
      populateChoices();
      choice = manualChoices.find(item => choiceKey(item) === choiceKey(data.current))
        || manualChoices.find(item => item.country === data.current.country)
        || manualChoices[0];
      choiceSelect.value = choiceKey(choice);
    }
    pendingRecommendation = null;
    returnFocus = trigger || document.activeElement;
    // Explain the destination in a language the visitor understands before they accept it.
    const copyLanguage = mode === 'recommendation'
      ? policy.chooseLanguage({ languages: Object.keys(data.translations).map(code => ({ code })) }, navigator.languages, null, data.current.language)
      : data.current.language;
    setCopy(copyLanguage, mode, choice);
    setChoice(choice);
    dialog.showModal();
    document.documentElement.classList.add('market-localization-open');
    (mode === 'manual' ? choiceSelect : dialog.querySelector('[data-localization-submit]')).focus();
  }

  function dismissDialog() {
    const preference = readPreference();
    if (!preference || preference.kind !== 'selected') savePreference({ kind: 'dismissed', expiresAt: Date.now() + 7 * day });
    dialog.close();
  }

  function isVisible(element) {
    const style = getComputedStyle(element);
    return !element.hidden && style.visibility !== 'hidden' && style.display !== 'none' && element.getClientRects().length > 0;
  }

  function anotherOverlayIsOpen() {
    return [...document.querySelectorAll('dialog[open], [role="dialog"], [aria-modal="true"], .NewsletterPopup[aria-hidden="false"], .WI_newsPopup_main')]
      .some(element => element !== dialog && !dialog.contains(element) && isVisible(element));
  }

  function offerRecommendation() {
    if (!pendingRecommendation || document.visibilityState !== 'visible' || dialog.open) return;
    if (readPreference()) {
      pendingRecommendation = null;
      return;
    }
    if (anotherOverlayIsOpen()) return;
    openDialog(pendingRecommendation, 'recommendation');
  }

  async function findRecommendation() {
    if (readPreference()) return;
    const cacheKey = `bark:localization:suggestion:${data.current.country}:${data.current.language}`;
    let cached;
    try {
      cached = JSON.parse(sessionStorage.getItem(cacheKey));
    } catch (error) {
      cached = null;
    }
    if (cached && cached.expiresAt > Date.now()
      && (cached.choice === null ? policy.validChoice(data.current, data.countries) : policy.validChoice(cached.choice, data.countries))) {
      pendingRecommendation = cached.choice;
      offerRecommendation();
      return;
    }

    const url = new URL(`${data.current.root.replace(/\/$/, '')}/browsing_context_suggestions.json`, location.origin);
    url.search = new URLSearchParams({
      'country[enabled]': 'true', 'country[exclude]': data.current.country,
      'language[enabled]': 'true', 'language[exclude]': data.current.language
    });
    try {
      const response = await fetch(url, { signal: AbortSignal.timeout(5000), credentials: 'same-origin' });
      if (!response.ok) return;
      const choice = policy.getRecommendation(data.current, data.countries, await response.json(), navigator.languages);
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({ choice, expiresAt: Date.now() + 30 * 60 * 1000 }));
      } catch (error) {
        // The recommendation still works without a session cache.
      }
      pendingRecommendation = choice;
      offerRecommendation();
    } catch (error) {
      // A slow or unavailable recommendation service must not interrupt shopping.
    }
  }

  choiceSelect.addEventListener('change', () => {
    const choice = manualChoices.find(item => choiceKey(item) === choiceSelect.value);
    if (choice) setChoice(choice);
  });
  // Desktop header: a hover/click/keyboard menu sharing the dialog form and cart transfer.
  const hoverCapable = matchMedia('(hover: hover) and (pointer: fine)');
  const menus = [...document.querySelectorAll('[data-localization-menu]')].map((menu, index) => {
    const wrapper = menu.parentElement;
    const trigger = wrapper && wrapper.querySelector('[data-localization-open]');
    if (!trigger || !manualChoices.length) {
      menu.remove();
      return null;
    }
    menu.id = menu.id || `MarketLanguageMenu-${index + 1}`;
    menu.setAttribute('aria-label', copyFor(data.current.language).title);
    trigger.setAttribute('aria-haspopup', 'menu');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', menu.id);
    // Same preselection as the dialog: the current combination, or the eligible one for the current country.
    const current = manualChoices.find(item => choiceKey(item) === choiceKey(data.current))
      || manualChoices.find(item => item.country === data.current.country);
    menu.replaceChildren(...manualChoices.map(item => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'MarketLanguageSelector__MenuItem';
      button.setAttribute('role', 'menuitem');
      button.dataset.localizationMenuChoice = choiceKey(item);
      button.textContent = choiceLabel(item);
      if (item === current) button.setAttribute('aria-current', 'true');
      return button;
    }));
    return { wrapper, trigger, menu, closeTimer: null };
  }).filter(Boolean);

  function menuItems(state) {
    return [...state.menu.querySelectorAll('[role="menuitem"]')];
  }

  function menuError(state, text) {
    let error = state.menu.querySelector('.MarketLanguageSelector__MenuError');
    if (!text) {
      if (error) error.remove();
      return;
    }
    if (!error) {
      error = document.createElement('p');
      error.className = 'MarketLanguageSelector__MenuError';
      error.setAttribute('role', 'alert');
      state.menu.append(error);
    }
    error.textContent = text;
  }

  function openMenu(state, focusTarget) {
    clearTimeout(state.closeTimer);
    if (dialog.open) return;
    if (state.menu.hidden) {
      menus.forEach(other => other !== state && closeMenu(other));
      state.menu.hidden = false;
      state.trigger.setAttribute('aria-expanded', 'true');
    }
    if (focusTarget) {
      const items = menuItems(state);
      (items.find(item => item.getAttribute('aria-current') === 'true') || items[0])?.focus();
    }
  }

  function closeMenu(state, returnFocusToTrigger) {
    clearTimeout(state.closeTimer);
    if (state.menu.hidden) return;
    state.menu.hidden = true;
    state.trigger.setAttribute('aria-expanded', 'false');
    menuError(state, '');
    if (returnFocusToTrigger) state.trigger.focus({ preventScroll: true });
  }

  function chooseFromMenu(state, button) {
    const item = manualChoices.find(choice => choiceKey(choice) === button.dataset.localizationMenuChoice);
    if (!item) return;
    // Reuse the dialog form so the menu shares validation, cart transfer and preferences.
    populateChoices();
    options.hidden = false;
    choiceSelect.value = choiceKey(item);
    setChoice(item);
    menuError(state, '');
    form.requestSubmit();
    if (!errorMessage.hidden) {
      menuError(state, errorMessage.textContent);
      return;
    }
    menuItems(state).forEach(other => { other.disabled = true; });
    if (item.country === data.current.country && item.language === data.current.language) {
      menuItems(state).forEach(other => { other.disabled = false; });
      closeMenu(state, true);
    }
  }

  menus.forEach(state => {
    state.wrapper.addEventListener('pointerenter', () => {
      if (hoverCapable.matches) openMenu(state, false);
    });
    state.wrapper.addEventListener('pointerleave', () => {
      if (!hoverCapable.matches) return;
      clearTimeout(state.closeTimer);
      state.closeTimer = setTimeout(() => {
        if (!state.wrapper.contains(document.activeElement) || document.activeElement === state.trigger) closeMenu(state, false);
      }, 160);
    });
    state.wrapper.addEventListener('focusout', event => {
      if (!event.relatedTarget || !state.wrapper.contains(event.relatedTarget)) closeMenu(state, false);
    });
    state.trigger.addEventListener('keydown', event => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        openMenu(state, true);
      } else if (event.key === 'Escape' && !state.menu.hidden) {
        event.preventDefault();
        closeMenu(state, true);
      }
    });
    state.menu.addEventListener('keydown', event => {
      const items = menuItems(state);
      const index = items.indexOf(document.activeElement);
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closeMenu(state, true);
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        items[(index + step + items.length) % items.length]?.focus();
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        items[event.key === 'Home' ? 0 : items.length - 1]?.focus();
      } else if (event.key === 'Tab') {
        // Tab leaves the menu: continue the tab sequence from the trigger with the menu closed.
        closeMenu(state, true);
      }
    });
    state.menu.addEventListener('click', event => {
      const button = event.target.closest('[data-localization-menu-choice]');
      if (button && !button.disabled) chooseFromMenu(state, button);
    });
  });

  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-localization-open]');
    const owner = trigger && menus.find(state => state.trigger === trigger);
    menus.forEach(state => {
      if (state !== owner && !state.wrapper.contains(event.target)) closeMenu(state, false);
    });
    if (!trigger) return;
    event.preventDefault();
    if (owner) {
      // Keyboard activation (detail 0) moves focus into the menu; a click after hover keeps it open.
      if (owner.menu.hidden) openMenu(owner, !event.detail);
      else if (!hoverCapable.matches) closeMenu(owner, false);
      return;
    }
    openDialog({ country: data.current.country, language: data.current.language }, 'manual', trigger);
  });
  dialog.querySelector('[data-localization-close]').addEventListener('click', dismissDialog);
  dialog.querySelector('[data-localization-stay]').addEventListener('click', dismissDialog);
  dialog.addEventListener('cancel', event => {
    event.preventDefault();
    dismissDialog();
  });
  dialog.addEventListener('close', () => {
    document.documentElement.classList.remove('market-localization-open');
    if (returnFocus && returnFocus.isConnected) returnFocus.focus({ preventScroll: true });
  });
  async function transferCart(destination, pending) {
    const activeMenu = menus.find(state => !state.menu.hidden);
    savePreference(pending);
    dialog.querySelector('[data-localization-submit]').disabled = true;
    menus.forEach(state => menuItems(state).forEach(item => { item.disabled = true; }));
    try {
      await window.BarkCartTransfer.navigate(destination);
    } catch (error) {
      // A failed asset request must also leave the source cart and selector usable.
      const message = window.BarkCartTransfer ? window.BarkCartTransfer.switchError() : copyFor(dialog.lang).unavailable;
      errorMessage.textContent = message;
      errorMessage.hidden = false;
      dialog.querySelector('[data-localization-submit]').disabled = false;
      menus.forEach(state => {
        menuItems(state).forEach(item => { item.disabled = false; });
      });
      if (activeMenu) {
        openMenu(activeMenu, true);
        menuError(activeMenu, message);
      }
    }
  }

  form.addEventListener('submit', event => {
    const choice = { country: countryInput.value, language: languageInput.value };
    const manualChoice = options.hidden ? null : manualChoices.find(item => choiceKey(item) === choiceSelect.value);
    if (!options.hidden && (!manualChoice || choice.country !== (manualChoice.country || '') || choice.language !== manualChoice.language)) {
      event.preventDefault();
      return;
    }

    const returnTo = new URL(location.href);
    returnTo.searchParams.delete('country');
    returnTo.searchParams.delete('language');
    returnTo.searchParams.delete('locale');
    returnTo.searchParams.delete(handoffKey);

    if (manualChoice && !manualChoice.country) {
      event.preventDefault();
      // Shopify's alternate URL resolves translated handles and the multi-country market's own default.
      const hreflangs = new Set(manualChoice.countries.map(code => `${manualChoice.language}-${code}`.toLowerCase()));
      const alternate = alternateLinks().find(link => hreflangs.has(link.hreflang.toLowerCase()));
      if (!alternate) {
        errorMessage.textContent = copyFor(dialog.lang).unavailable;
        errorMessage.hidden = false;
        return;
      }
      const destination = new URL(alternate.href);
      destination.search = returnTo.search;
      destination.hash = returnTo.hash;
      const pending = { kind: 'pending', market: manualChoice.market, currency: manualChoice.currency, language: manualChoice.language, expiresAt: Date.now() + pendingLifetime };
      if (destination.origin !== location.origin) destination.searchParams.set(handoffKey, encodeHandoff(pending));
      if (destination.origin !== location.origin) {
        transferCart(destination, pending);
      } else {
        savePreference(pending);
        dialog.querySelector('[data-localization-submit]').disabled = true;
        location.assign(destination.href);
      }
      return;
    }
    if (!policy.validChoice(choice, data.countries)) {
      event.preventDefault();
      return;
    }
    if (choice.country === data.current.country && choice.language === data.current.language) {
      event.preventDefault();
      savePreference({ ...choice, kind: 'selected', expiresAt: Date.now() + 60 * day });
      dialog.close();
      return;
    }

    // Shopify resolves translated handles, availability and the market domain. Do not manufacture URLs.
    const pending = { ...choice, kind: 'pending', expiresAt: Date.now() + pendingLifetime };
    const origin = destinationOrigin(choice);
    if (!origin) {
      event.preventDefault();
      errorMessage.textContent = copyFor(dialog.lang).unavailable;
      errorMessage.hidden = false;
      return;
    }
    if (origin !== location.origin) {
      event.preventDefault();
      const codes = [`${choice.language}-${choice.country}`, choice.language].map(code => code.toLowerCase());
      const alternate = codes.map(code => alternateLinks().find(link => link.hreflang.toLowerCase() === code)).find(Boolean);
      const destination = new URL(alternate.href);
      destination.search = returnTo.search;
      destination.hash = returnTo.hash;
      destination.searchParams.set(handoffKey, encodeHandoff(pending));
      transferCart(destination, pending);
      return;
    }
    form.elements.return_to.value = returnTo.pathname + returnTo.search + returnTo.hash;
    savePreference(pending);
    dialog.querySelector('[data-localization-submit]').disabled = true;
  });
  window.addEventListener('pageshow', event => {
    dialog.querySelector('[data-localization-submit]').disabled = false;
    menus.forEach(state => menuItems(state).forEach(item => { item.disabled = false; }));
    if (event.persisted) {
      pagePreference = null;
      confirmPendingChoice();
      observeOverlays();
      offerRecommendation();
    }
  });
  document.addEventListener('visibilitychange', offerRecommendation);
  document.addEventListener('visitorConsentCollected', offerRecommendation);
  // Wait for existing cookie/newsletter dialogs to close instead of stacking dialogs.
  const observer = new MutationObserver(offerRecommendation);
  function observeOverlays() {
    observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['open', 'hidden', 'aria-hidden', 'style', 'class'] });
  }
  observeOverlays();
  window.addEventListener('pagehide', () => {
    observer.disconnect();
    if (dialog.open) dialog.close();
    document.documentElement.classList.remove('market-localization-open');
  });
  consumeHandoff();
  confirmPendingChoice();
  findRecommendation();
}());
